import { apiClient } from './client';
import { Episode, PaginatedResponse } from '../../types';

export interface CreateEpisodeRequest {
  chap?: number;
  thumbnailUrl?: string;
  videoUrl?: string;
  duration?: number;
  status?: string;
  isLocked?: boolean;
  unlockCondition?: string;
}

export interface UpdateEpisodeRequest extends Partial<CreateEpisodeRequest> {}

export interface GetEpisodesParams {
  pageIndex?: number;
  pageSize?: number;
  status?: string;
}

export const episodesApi = {
  // Get episodes for a story
  getEpisodes: async (storyId: string, params?: GetEpisodesParams): Promise<PaginatedResponse<Episode>> => {
    return apiClient.get<PaginatedResponse<Episode>>(`/api/v1/admin/stories/${storyId}/episodes`, { params });
  },

  // Get episode detail
  getEpisodeDetail: async (storyId: string, episodeId: string): Promise<{ data: Episode }> => {
    return apiClient.get<{ data: Episode }>(`/api/v1/admin/stories/${storyId}/episodes/${episodeId}`);
  },

  // Create new episode
  createEpisode: async (
    storyId: string, 
    data: CreateEpisodeRequest, 
    thumbnailFile?: File, 
    videoFile?: File,
    onProgress?: (progress: number) => void
  ): Promise<{ data: Episode }> => {
    // Only use FormData when there are file uploads
    if (thumbnailFile || videoFile) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      
      if (thumbnailFile) {
        formData.append('thumbnailImage', thumbnailFile);
      }
      
      if (videoFile) {
        formData.append('video', videoFile);
      }
      
      return apiClient.upload<{ data: Episode }>(`/api/v1/admin/stories/${storyId}/episodes`, formData, onProgress);
    } else {
      // Use JSON request when there's no file
      return apiClient.post<{ data: Episode }>(`/api/v1/admin/stories/${storyId}/episodes`, data);
    }
  },

  // Update episode
  updateEpisode: async (
    storyId: string, 
    episodeId: string, 
    data: UpdateEpisodeRequest, 
    thumbnailFile?: File, 
    videoFile?: File,
    onProgress?: (progress: number) => void
  ): Promise<{ data: Episode }> => {
    // Only use FormData when there are file uploads
    if (thumbnailFile || videoFile) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      
      if (thumbnailFile) {
        formData.append('thumbnailImage', thumbnailFile);
      }
      
      if (videoFile) {
        formData.append('video', videoFile);
      }
      
      return apiClient.uploadPut<{ data: Episode }>(`/api/v1/admin/stories/${storyId}/episodes/${episodeId}`, formData, onProgress);
    } else {
      // Use JSON request when there's no file
      return apiClient.put<{ data: Episode }>(`/api/v1/admin/stories/${storyId}/episodes/${episodeId}`, data);
    }
  },

  // Delete episode
  deleteEpisode: async (storyId: string, episodeId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/stories/${storyId}/episodes/${episodeId}`);
  },

  // Get video processing status
  getProcessingStatus: async (episodeId: string): Promise<{ 
    data: {
      jobId: string;
      episodeId: string;
      status: string;
      progress: number;
      errorMessage?: string;
      qualities?: any;
    }
  }> => {
    return apiClient.get<any>(`/api/v1/admin/episodes/${episodeId}/processing-status`);
  },

  // Trigger video processing
  triggerVideoProcessing: async (storyId: string, episodeId: string): Promise<{
    data: {
      episodeId: string;
      jobId: string;
      status: string;
      message: string;
    }
  }> => {
    return apiClient.post<any>(`/api/v1/admin/stories/${storyId}/episodes/${episodeId}/process-video`);
  },

  // Reorder episodes (drag and drop)
  reorderEpisodes: async (storyId: string, orderedEpisodeIds: string[]): Promise<{ data: Episode[] }> => {
    return apiClient.put<{ data: Episode[] }>(
      `/api/v1/admin/stories/${storyId}/episodes/reorder`,
      { orderedEpisodeIds }
    );
  },

  // Epic 8: Upload subtitle file for a specific language
  uploadSubtitle: async (
    storyId: string,
    episodeId: string,
    subtitleFile: File,
    language: string,
    label: string,
    onProgress?: (progress: number) => void
  ): Promise<{ data: Episode }> => {
    const formData = new FormData();
    formData.append('subtitleFile', subtitleFile);
    formData.append('data', JSON.stringify({ subtitleLanguage: language, subtitleLabel: label }));
    return apiClient.uploadPut<{ data: Episode }>(
      `/api/v1/admin/stories/${storyId}/episodes/${episodeId}`,
      formData,
      onProgress
    );
  },

  // Epic 8: Set subtitle URL directly (no file upload)
  setSubtitleUrl: async (
    storyId: string,
    episodeId: string,
    language: string,
    label: string,
    url: string
  ): Promise<{ data: Episode }> => {
    return apiClient.put<{ data: Episode }>(
      `/api/v1/admin/stories/${storyId}/episodes/${episodeId}`,
      { subtitles: { [language]: { url, label } } }
    );
  },

  // Epic 8: Delete subtitle for a specific language
  deleteSubtitle: async (
    storyId: string,
    episodeId: string,
    language: string
  ): Promise<{ success: boolean }> => {
    return apiClient.delete<{ success: boolean }>(
      `/api/v1/admin/stories/${storyId}/episodes/${episodeId}/subtitles/${language}`
    );
  },
};